import React, { useCallback, useMemo, useEffect, useState } from "react";
import { Button, Modal } from "src/assets/imports/antdComponents";
import "src/assets/styles/antdAnimation.css";
import "./style.css";

export interface GenericModalProps {
	isVisible: boolean;
	setIsVisible: (visible: boolean) => void;
	title: string;
	width?: number;
	children: React.ReactNode;
	onSave?: () => void;
	onCancel?: () => void;
	saveButtonText?: string;
	cancelButtonText?: string;
	saveButtonDisabled?: boolean;
	saveButtonLoading?: boolean;
	modalPosition?: { top?: number; left?: number; right?: number };
	modalStyles?: any;
	hideSaveButton?: boolean;
	hideCancelButton?: boolean;
	disableCancelButton?: boolean;
	customFooter?: React.ReactNode;
	isHomePage?: boolean;
}

const defaultModalStyles = {
	header: { height: "4rem", padding: "1rem", margin: 0, fontSize: "10px" },
	body: { padding: "1.5rem" },
	mask: { backdropFilter: "blur(2px)", backgroundColor: "transparent" },
	footer: { padding: "1rem 1.5rem", paddingTop: "0", margin: 0 },
	content: { padding: 0, margin: 0, transformOrigin: "top left" },
};

// Function to get scaled position based on screen width
const getScaledPosition = (modalPosition?: { top?: number; left?: number; right?: number }, isHomePage = false) => {
	if (!modalPosition) return undefined;

	const width = window.innerWidth;

	// Default values (1920px and above - scale 1.0)
	let scaledTop = modalPosition.top;
	let scaledLeft = modalPosition.left;
	let scaledRight = modalPosition.right;

	if (!isHomePage) {
		return {
			top: scaledTop,
			left: scaledLeft,
			right: scaledRight,
		};
	}

	// Scale 90% (1400px - 1699px)
	if (width >= 1540 && width <= 1709) {
		if (modalPosition.top !== undefined) scaledTop = 431;
		if (modalPosition.left !== undefined) scaledLeft = 1105;
	}
	// Scale 100% (1250px - 1399px)
	else if (width >= 1400 && width <= 1539) {
		if (modalPosition.top !== undefined) scaledTop = 400;
		if (modalPosition.left !== undefined) scaledLeft = 985;
	}
	// Scale 110%  (1250px - 1399px)
	else if (width >= 1250 && width <= 1399) {
		if (modalPosition.top !== undefined) scaledTop = 352;
		if (modalPosition.left !== undefined) scaledLeft = 912;
	}
	// Scale 125% (below 1250px)
	else if (width < 1250) {
		if (modalPosition.top !== undefined) scaledTop = 306;
		if (modalPosition.left !== undefined) scaledLeft = 812;
	}

	return {
		top: scaledTop,
		left: scaledLeft,
		right: scaledRight,
	};
};

const GenericCreateModal: React.FC<GenericModalProps> = ({
	isVisible,
	setIsVisible,
	title,
	width = 499,
	children,
	onSave,
	onCancel,
	saveButtonText = "Save",
	cancelButtonText = "Cancel",
	saveButtonDisabled = false,
	saveButtonLoading = false,
	modalPosition,
	modalStyles = defaultModalStyles,
	hideSaveButton = false,
	hideCancelButton = false,
	disableCancelButton = false,
	customFooter,
	isHomePage = false,
}) => {
	const [scaledPosition, setScaledPosition] = useState(getScaledPosition(modalPosition, isHomePage));

	useEffect(() => {
		const handleResize = () => {
			setScaledPosition(getScaledPosition(modalPosition, isHomePage));
		};

		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, [modalPosition, isHomePage]);

	const handleCancel = useCallback(() => {
		if (onCancel) {
			onCancel();
		} else {
			setIsVisible(false);
		}
	}, [onCancel, setIsVisible]);

	const handleSave = useCallback(() => {
		if (onSave) {
			onSave();
		}
	}, [onSave]);

	const footer = useMemo(
		() =>
			customFooter || (
				<div className="flex justify-end gap-3">
					{!hideCancelButton && (
						<Button type="text" className="text-[--warning-red]" onClick={handleCancel} disabled={disableCancelButton}>
							{cancelButtonText}
						</Button>
					)}
					{!hideSaveButton && (
						<Button loading={saveButtonLoading} disabled={saveButtonDisabled} htmlType="submit" type="primary" onClick={handleSave}>
							{saveButtonText}
						</Button>
					)}
				</div>
			),
		[customFooter, hideCancelButton, handleCancel, disableCancelButton, cancelButtonText, hideSaveButton, saveButtonLoading, saveButtonDisabled, handleSave, saveButtonText],
	);

	return (
		<Modal
			title={title}
			open={isVisible}
			closable={false}
			maskClosable={false}
			mask={true}
			width={width}
			centered={false}
			transitionName=""
			maskTransitionName=""
			wrapClassName={isHomePage ? "generic-create-modal-scaled" : undefined}
			style={
				isVisible && scaledPosition
					? {
							top: scaledPosition.top || 110,
							right: scaledPosition.right,
							left: scaledPosition.left,
							margin: 0,
							position: "absolute",
							transformOrigin: "top left",
						}
					: undefined
			}
			styles={modalStyles}
			footer={footer}>
			{children}
		</Modal>
	);
};

export default GenericCreateModal;
